import { HeroMinimal, BookingCTA } from "../components/sections";
import { SectionHeader, FeatureCard } from "../components/sections";
import { Comparison } from "../components/sections/Comparison";
import { ServiceComparisonCharts } from "../components/sections/ServiceComparisonCharts";
import {
  CheckCircle,
  Brain,
  Zap,
  Shield,
  Users,
  Target,
  Database,
  RefreshCw,
  XCircle,
} from "lucide-react";

const comparison = [
  {
    dimension: "Approach",
    traditional: "Campaign-first",
    uniqreach: "System-first",
  },
  {
    dimension: "Focus",
    traditional: "Activity metrics (emails sent)",
    uniqreach: "Outcome metrics (qualified conversations)",
  },
  {
    dimension: "Workflows",
    traditional: "Manual & disconnected",
    uniqreach: "Automated & integrated",
  },
  {
    dimension: "Data",
    traditional: "Generic lists, limited verification",
    uniqreach: "Multi-source enrichment + real-time verification",
  },
  {
    dimension: "Intelligence",
    traditional: "Basic merge tags",
    uniqreach: "AI research, classification, personalization",
  },
  {
    dimension: "Infrastructure",
    traditional: "Shared/basic sending",
    uniqreach: "Dedicated domains, authentication, deliverability discipline",
  },
  {
    dimension: "Optimization",
    traditional: "Post-campaign reporting",
    uniqreach: "Continuous closed-loop improvement",
  },
  {
    dimension: "Scalability",
    traditional: "Linear (more people = more output)",
    uniqreach: "Repeatable workflows that can grow with your sales process",
  },
];

const differentiators = [
  {
    title: "Sales-First Thinking",
    description:
      "Built by people who have carried quotas. Every system design decision optimizes for sales team efficiency and qualified conversations rather than vanity metrics.",
    icon: Target,
  },
  {
    title: "AI-Native Workflows",
    description:
      "AI isn't bolted on; it's architected into research, classification, personalization, qualification, and optimization from day one.",
    icon: Brain,
  },
  {
    title: "Automation Depth",
    description:
      "Not just sequence automation. Workflow automation: research → enrichment → personalization → outreach → CRM → optimization. End-to-end.",
    icon: Zap,
  },
  {
    title: "Data Discipline",
    description:
      "Verification-first philosophy. Multi-source enrichment. Continuous hygiene. Quality gates at every stage. Relevance over volume.",
    icon: Database,
  },
  {
    title: "Infrastructure Expertise",
    description:
      "Domains, inboxes, authentication, deliverability, sending patterns, and the foundation that supports responsible outbound.",
    icon: Shield,
  },
  {
    title: "Compound Scalability",
    description:
      "The system learns. Every response, meeting, and deal retrains models, refines segments, and improves personalization. Performance compounds.",
    icon: RefreshCw,
  },
  {
    title: "Qualified Conversations",
    description:
      "The only metric that matters. Prospects who match ICP, have verified intent, and are ready for a sales conversation.",
    icon: Users,
  },
  {
    title: "Human Oversight Where It Matters",
    description:
      'AI executes; humans strategize, approve, and close. Clear responsibility boundaries. No "AI magic" black boxes.',
    icon: CheckCircle,
  },
];

const philosophy = [
  "Automation creates leverage. AI increases intelligence. Humans close the relationship.",
  "Systems compound. Campaigns decay.",
  "Better data beats more data.",
  "Infrastructure enables scale. Strategy directs it.",
  "Qualification is the product. Outreach is the delivery mechanism.",
  "Transparency builds trust. Specificity proves competence.",
];

function WhyUniqReachPage() {
  return (
    <>
      <HeroMinimal
        title="Systems, Not Campaigns."
        subtitle="The fundamental difference: campaigns are finite, linear, and activity-focused. Systems are continuous, compounding, and outcome-focused."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See Results"
        secondaryCtaHref="/results"
      />

      <section className="section" aria-labelledby="service-models-title">
        <div className="container">
          <SectionHeader
            id="service-models-title"
            eyebrow="The difference, visualized"
            title="Compare the scope. Then compare the process."
            description="Two views of how a connected sales system differs from a limited service or a finite campaign."
          />
          <ServiceComparisonCharts />
        </div>
      </section>

      {/* Comparison */}
      <section className="section" aria-labelledby="comparison-title">
        <div className="container">
          <SectionHeader
            id="comparison-title"
            eyebrow="Comparison"
            title="Traditional Agency vs. UniqReach"
            description="A direct comparison across the dimensions that determine outbound success."
            align="center"
          />
          <Comparison rows={comparison} />
        </div>
      </section>

      {/* Differentiators */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="differentiators-title"
      >
        <div className="container">
          <SectionHeader
            id="differentiators-title"
            eyebrow="Differentiators"
            title="Eight Reasons Companies Choose UniqReach"
            description="The practical decisions behind a more connected sales process."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {differentiators.map((diff, index) => (
              <FeatureCard
                key={diff.title}
                icon={<diff.icon size={24} className="text-accent" />}
                title={diff.title}
                description={diff.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section" aria-labelledby="philosophy-title">
        <div className="container">
          <SectionHeader
            id="philosophy-title"
            eyebrow="Core Philosophy"
            title="The Principles Behind the System"
            description="These aren't slogans. They're the decision-making framework for every system we build."
            align="center"
          />
          <div className="max-w-3xl mx-auto mt-12 space-y-4">
            {philosophy.map((principle, index) => (
              <div
                key={index}
                className="card p-6 flex items-start gap-4 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 text-accent">
                  <CheckCircle size={20} aria-hidden="true" />
                </div>
                <p className="body text-text-secondary pt-1">{principle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="notdo-title"
      >
        <div className="container">
          <SectionHeader
            id="notdo-title"
            eyebrow="Boundaries"
            title="What We Don't Do"
            description="Clear boundaries prevent scope creep and ensure we only engage where we create leverage."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {[
              "Guarantee meetings or pipeline",
              "Send on your primary domain",
              "Use purchased or scraped lists",
              "Operate without verified ICP",
              "Promise instant results",
              "Work without human oversight",
              "Hide our methodology",
              "Compete on volume metrics",
            ].map((item, index) => (
              <div
                key={index}
                className="card p-5 flex items-center gap-3 animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <XCircle
                  size={20}
                  className="text-error flex-shrink-0"
                  aria-hidden="true"
                />
                <p className="body text-text-secondary">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default WhyUniqReachPage;
